import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, notify, confirmDialog, EmptyState, genToken } from "./registry";
/* ============================================================
   Fence Works — Reviews
   Every review the contractor collects, in one place (server/src/reviews.js):
   • Inbox      Google reviews (synced), on-site ratings + private notes, and
                reviews added by hand. Reply to Google in one tap (AI drafts),
                feature or hide reviews on the website, resolve low ratings.
   • Campaigns  "Reactivation": a feedback-first review ask to past customers,
                staggered, consent-aware, stops when someone answers.
   • Share      the hosted reviews page + the live feed for their website.
   Rules this screen never bends: private feedback is never published, every
   ask offers the public review to every rating (no gating), and nothing offers
   an incentive for a review.
   ============================================================ */

const DAY = 86400000;
const SRC_LABEL: any = { google: "Google", onsite: "Feedback page", facebook: "Facebook", manual: "Added by hand", other: "Other" };
const FILTERS: any[] = [
  ["all", "All"], ["google", "Google"], ["onsite", "Feedback page"], ["other", "Other sources"],
  ["reply", "Needs reply"], ["low", "3★ & under"], ["private", "Private"],
];
const TABS: any[] = [["inbox", "Inbox"], ["campaigns", "Past-customer campaign"], ["share", "Share & website"]];

const fmtDate = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""; };
const ago = (iso: any) => {
  const t = Date.parse(iso || ""); if (!t) return "";
  const d = Math.floor((Date.now() - t) / DAY);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : d < 30 ? d + " days ago" : fmtDate(iso);
};
function Stars({ n, size = 14 }: any) {
  const k = Math.round(Number(n) || 0);
  return <span aria-label={k + " out of 5 stars"} style={{ color: "#f5b301", letterSpacing: 1.5, fontSize: size, whiteSpace: "nowrap" }}>{"★★★★★".slice(0, k)}<span style={{ color: "var(--line-2)" }}>{"★★★★★".slice(0, 5 - k)}</span></span>;
}

/* Sample data for the sandbox / local dev (no service to call). */
const SAMPLE = (() => {
  const d = (n: number) => new Date(Date.now() - n * DAY).toISOString();
  const reviews = [
    { id: "s1", source: "google", visibility: "public", rating: 5, author_name: "Megan Olsen", body: "Kodiak put in a white vinyl privacy fence for us in two days. Crew was polite, cleaned up every scrap, and the gate latches perfectly.", reviewed_at: d(2), reply_draft: "Thanks Megan — the crew loved working on your place. Enjoy the privacy, and holler if that gate ever needs a tweak.", reply_state: "draft", featured: true },
    { id: "s2", source: "google", visibility: "public", rating: 5, author_name: "Tom Brandt", body: "Fair quote, showed up when they said, fence is rock solid.", reviewed_at: d(9), reply_text: "Appreciate you, Tom. Rock solid is the goal!", reply_at: d(8) },
    { id: "s3", source: "onsite", visibility: "private", rating: 3, author_name: "Jill Kowalski", body: "Fence looks great but the gate sags a little already.", reviewed_at: d(4) },
    { id: "s4", source: "google", visibility: "public", rating: 4, author_name: "Rick Janssen", body: "Good work. Took a week longer than planned because of permits.", reviewed_at: d(21) },
    { id: "s5", source: "facebook", visibility: "public", rating: 5, author_name: "Ana Ruiz", body: "Our dog can finally play in the yard. Thank you!", reviewed_at: d(40) },
  ];
  return { reviews, sample: true, google: { connected: false }, summary: null };
})();

function summaryOf(rows: any[]) {
  const pub = rows.filter((r) => r.visibility === "public" && r.rating);
  const priv = rows.filter((r) => r.visibility === "private" && r.rating);
  const now = Date.now();
  const t = (r: any) => Date.parse(r.reviewed_at || "") || 0;
  return {
    publicCount: pub.length,
    publicAverage: pub.length ? Math.round((pub.reduce((a, r) => a + r.rating, 0) / pub.length) * 10) / 10 : null,
    privateCount: priv.length,
    privateAverage: priv.length ? Math.round((priv.reduce((a, r) => a + r.rating, 0) / priv.length) * 10) / 10 : null,
    last30: rows.filter((r) => now - t(r) <= 30 * DAY).length,
    prev30: rows.filter((r) => now - t(r) > 30 * DAY && now - t(r) <= 60 * DAY).length,
    needsReply: rows.filter((r) => r.source === "google" && !r.reply_text).length,
    lowUnresolved: rows.filter((r) => r.rating && r.rating <= 3 && !r.reply_text && !r.resolved_at).length,
  };
}

function ReviewsPage({ store, go, params }: any) {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [tab, setTab] = React.useState<any>((params && params.tab) || "inbox");
  const [data, setData] = React.useState<any>(live ? null : SAMPLE);
  const [err, setErr] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>("");
  const [adding, setAdding] = React.useState<any>(false);

  const load = React.useCallback(async () => {
    if (!live) return;
    try { setErr(""); setData(await DB.reviewsList()); }
    catch (e: any) { setErr(e.code === "migration" ? "migration" : String(e.message || e)); setData({ reviews: [], google: {} }); }
  }, [live]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { if (params && params.tab) setTab(params.tab); }, [params && params.tab]);

  const rows = (data && data.reviews) || [];
  const s = summaryOf(rows);
  const settings = store.settings || {};
  const patchLocal = (id: string, p: any) => setData((d: any) => ({ ...d, reviews: d.reviews.map((r: any) => (r.id === id ? { ...r, ...p } : r)) }));

  const sync = async () => {
    setBusy("sync");
    try { const r = await DB.reviewsSync(); notify(`Synced ${r.stored} Google review${r.stored === 1 ? "" : "s"}.`); await load(); }
    catch (e: any) { notify(e.message || "Couldn't sync with Google", { kind: "error" }); }
    setBusy("");
  };

  return (
    <div className="page fade-up" style={{ maxWidth: 1080 }}>
      <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="kicker kicker-teal">Reputation</span>
          <h1 className="display" style={{ fontSize: 28, margin: "6px 0 4px" }}>Reviews</h1>
          <p className="muted" style={{ margin: 0, maxWidth: 620 }}>Every review you collect — Google, your feedback page, and the ones you add — in one place. Reply fast, fix what's wrong, and put the best ones on your website.</p>
        </div>
        <div className="row gap-8" style={{ alignSelf: "flex-start", flexWrap: "wrap" }}>
          {data && data.sample && <span className="chip amber" title="Connect to the service to see your real reviews">Sample data</span>}
          {live && data && data.google && data.google.connected && <button className="btn btn-ghost btn-sm" disabled={busy === "sync"} onClick={sync}><Icon name="undo" size={14} />{busy === "sync" ? "Syncing…" : "Sync Google"}</button>}
          <button className="btn btn-ghost btn-sm" onClick={() => go("gbp")}><Icon name="target" size={14} />Google Profile score</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)} disabled={!live}><Icon name="plus" size={14} />Add a review</button>
        </div>
      </div>

      <div className="cat-tabs" style={{ marginBottom: 18 }}>
        {TABS.map(([id, lb]) => <button key={id} className={"filter-chip" + (tab === id ? " on" : "")} onClick={() => setTab(id)}>{lb}</button>)}
      </div>

      {err === "migration" && (
        <div className="card pad" style={{ marginBottom: 16, borderColor: "var(--amber-tint)" }}>
          <b>One database update to go.</b>
          <p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>Reviews need <code>supabase/migrate_2026-09-28_reviews.sql</code> run once. Your review automations keep working in the meantime.</p>
        </div>
      )}
      {err && err !== "migration" && <div className="card pad" style={{ marginBottom: 16, color: "var(--red)" }}>{err}</div>}

      {tab === "inbox" && (
        !data ? <div className="card pad muted">Loading reviews…</div> :
        <ReviewsInbox rows={rows} s={s} data={data} live={live} go={go} settings={settings} patchLocal={patchLocal} reload={load} />
      )}
      {tab === "campaigns" && <ReviewCampaigns live={live} go={go} store={store} />}
      {tab === "share" && <ReviewsShare store={store} live={live} s={s} go={go} />}

      {adding && <AddReviewModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

/* ---------------- Inbox ---------------- */
function ReviewsInbox({ rows, s, data, live, go, settings, patchLocal, reload }: any) {
  const [filter, setFilter] = React.useState<any>("all");
  const [q, setQ] = React.useState<any>("");
  const list = rows.filter((r: any) => {
    if (filter === "google" && r.source !== "google") return false;
    if (filter === "onsite" && r.source !== "onsite") return false;
    if (filter === "other" && ["google", "onsite"].includes(r.source)) return false;
    if (filter === "reply" && !(r.source === "google" && !r.reply_text)) return false;
    if (filter === "low" && !(r.rating && r.rating <= 3)) return false;
    if (filter === "private" && r.visibility !== "private") return false;
    if (q && !((r.author_name || "") + " " + (r.body || "")).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const trend = s.last30 - s.prev30;
  return (
    <>
      <div className="rv-stats">
        <div className="card rv-stat">
          <span className="muted">Public rating</span>
          <b className="display tnum">{s.publicAverage != null ? s.publicAverage : "—"}{s.publicAverage != null && <span style={{ fontSize: 15, marginLeft: 6 }}><Stars n={s.publicAverage} size={15} /></span>}</b>
          <span className="faint">{s.publicCount} public review{s.publicCount === 1 ? "" : "s"}</span>
        </div>
        <div className="card rv-stat">
          <span className="muted">New · 30 days</span>
          <b className="display tnum">{s.last30}</b>
          <span className="faint" style={{ color: trend > 0 ? "var(--green)" : trend < 0 ? "var(--amber)" : undefined }}>{trend === 0 ? "same as the 30 before" : (trend > 0 ? "+" : "") + trend + " vs the 30 before"}</span>
        </div>
        <button className="card rv-stat hover" onClick={() => setFilter("reply")}>
          <span className="muted">Waiting on a reply</span>
          <b className="display tnum" style={{ color: s.needsReply ? "var(--amber)" : undefined }}>{s.needsReply}</b>
          <span className="faint">Google rewards owners who answer</span>
        </button>
        <button className="card rv-stat hover" onClick={() => setFilter("private")}>
          <span className="muted">Private feedback</span>
          <b className="display tnum">{s.privateCount}{s.privateAverage != null && <span style={{ fontSize: 14, marginLeft: 6 }} className="muted">avg {s.privateAverage}★</span>}</b>
          <span className="faint">{s.lowUnresolved ? `${s.lowUnresolved} low rating${s.lowUnresolved === 1 ? "" : "s"} to resolve` : "only you see these"}</span>
        </button>
      </div>

      {data.google && !data.google.connected && !data.sample && (
        <div className="card pad row between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
          <div style={{ maxWidth: 640 }}>
            <b>Pull in every Google review</b>
            <p className="muted" style={{ fontSize: 13, margin: "3px 0 0" }}>Connect Google Business Profile and your whole review history lands here — with AI reply drafts you post in one tap.</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => go("integrations")}>Connect Google<Icon name="chevright" size={13} /></button>
        </div>
      )}
      {data.google && data.google.error && <p className="faint" style={{ fontSize: 12, margin: "-6px 0 12px" }}>Last Google sync problem: {data.google.error}</p>}
      {!settings.googleReviewUrl && (
        <div className="card pad row between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap", borderColor: "var(--amber-tint)" }}>
          <div style={{ maxWidth: 640 }}>
            <b>Add your Google review link</b>
            <p className="muted" style={{ fontSize: 13, margin: "3px 0 0" }}>Your feedback page, QR codes and campaigns send people there to post. The Google Profile screen can find it for you.</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => go("gbp")}>Find it<Icon name="chevright" size={13} /></button>
        </div>
      )}

      <div className="row between" style={{ gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div className="cat-tabs" style={{ margin: 0 }}>
          {FILTERS.map(([id, lb]) => <button key={id} className={"filter-chip" + (filter === id ? " on" : "")} onClick={() => setFilter(id)}>{lb}</button>)}
        </div>
        <input className="input" style={{ maxWidth: 240 }} placeholder="Search name or text" value={q} onChange={(e: any) => setQ(e.target.value)} />
      </div>

      {!list.length ? (
        <div className="card">
          <EmptyState icon="chat" title={rows.length ? "Nothing matches that filter" : "No reviews yet"}
            sub={rows.length ? "Try another filter." : "Connect Google, turn on the review request after each job, or ask your past customers."}
            action={rows.length ? null : { label: "Ask past customers", icon: "send", onClick: () => go("reviews", { tab: "campaigns" }) }}
            secondary={rows.length ? null : { label: "Connect Google", onClick: () => go("integrations") }} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {list.map((r: any) => <ReviewCard key={r.id} r={r} live={live} patchLocal={patchLocal} reload={reload} go={go} />)}
        </div>
      )}
    </>
  );
}

function ReviewCard({ r, live, patchLocal, reload, go }: any) {
  const DB: any = (window as any).VFP_DB;
  const [draft, setDraft] = React.useState<any>(r.reply_draft || "");
  const [busy, setBusy] = React.useState<any>("");
  const [open, setOpen] = React.useState<any>(!!r.reply_draft);
  React.useEffect(() => { setDraft(r.reply_draft || ""); }, [r.reply_draft]);
  const isPrivate = r.visibility === "private";
  const low = r.rating && r.rating <= 3;

  const act = async (kind: string, fn: () => Promise<any>) => {
    if (!live) { notify("Sample data — connect to the service to do this for real."); return; }
    setBusy(kind);
    try { await fn(); } catch (e: any) { notify(e.message || "Something went wrong", { kind: "error" }); }
    setBusy("");
  };
  const writeAI = () => act("ai", async () => { const d = await DB.reviewDraft(r.id); setDraft(d.draft); setOpen(true); patchLocal(r.id, { reply_draft: d.draft, reply_state: "draft" }); });
  const post = () => act("post", async () => {
    if (r.source === "google" && !(await confirmDialog({ title: "Post this reply on Google?", body: "It appears publicly under the review right away. You can edit it later from Google.", confirmLabel: "Post reply" }))) return;
    const d = await DB.reviewReply(r.id, draft);
    patchLocal(r.id, d.review || { reply_text: draft, reply_at: new Date().toISOString(), reply_draft: null });
    notify(r.source === "google" ? "Reply posted on Google." : "Reply saved — it shows under the review on your website.");
  });
  const toggle = (field: string) => act(field, async () => { const d = await DB.reviewUpdate(r.id, { [field]: !r[field] }); patchLocal(r.id, d.review); });
  const resolve = () => act("resolve", async () => { const d = await DB.reviewUpdate(r.id, { resolved: !r.resolved_at }); patchLocal(r.id, d.review); });
  const remove = () => act("del", async () => {
    if (!(await confirmDialog({ title: "Delete this review?", body: "Only reviews you added by hand can be deleted.", confirmLabel: "Delete", danger: true }))) return;
    await DB.reviewDelete(r.id); reload();
  });

  return (
    <div className={"card rv-card" + (low && !r.reply_text && !r.resolved_at ? " rv-low" : "")}>
      <div className="row between" style={{ gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="row gap-12" style={{ alignItems: "flex-start" }}>
          <span className="rv-av">{r.author_photo ? <img src={r.author_photo} alt="" referrerPolicy="no-referrer" /> : (r.author_name || "?").slice(0, 1).toUpperCase()}</span>
          <div>
            <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <b>{r.author_name || "A customer"}</b>
              <Stars n={r.rating} />
            </div>
            <div className="faint" style={{ fontSize: 12 }}>{SRC_LABEL[r.source] || r.source} · {ago(r.reviewed_at)}</div>
          </div>
        </div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {isPrivate && <span className="chip violet" title="Left on your feedback page — never published">Private · only you see this</span>}
          {r.featured && <span className="chip teal">Featured</span>}
          {r.hidden && <span className="chip">Hidden from website</span>}
          {r.resolved_at && <span className="chip green">Resolved</span>}
        </div>
      </div>
      {r.body ? <p className="rv-body">{r.body}</p> : <p className="rv-body faint">{isPrivate ? "Left a star rating, no note." : "No written review — just the stars."}</p>}

      {r.reply_text ? (
        <div className="rv-reply"><span className="kicker">Your reply · {fmtDate(r.reply_at)}</span><p>{r.reply_text}</p></div>
      ) : isPrivate ? (
        <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 4 }}>
          {r.customer_id && <button className="btn btn-outline btn-sm" onClick={() => go("customers", { focus: r.customer_id })}><Icon name="user" size={13} />Open customer</button>}
          <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={resolve}><Icon name="check" size={13} />{r.resolved_at ? "Reopen" : "Mark resolved"}</button>
          {low && <span className="faint" style={{ fontSize: 12, alignSelf: "center" }}>Call them this week — a fixed problem often turns into a public 5★ on its own.</span>}
        </div>
      ) : (
        <div style={{ marginTop: 4 }}>
          {!open ? (
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={writeAI}><Icon name="bolt" size={13} />{busy === "ai" ? "Writing…" : "Write reply with AI"}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}><Icon name="edit" size={13} />Write my own</button>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <textarea className="textarea" rows={3} value={draft} placeholder={r.source === "google" ? "Your public reply on Google" : "Your reply (shows under the review on your website)"} onChange={(e: any) => setDraft(e.target.value)} />
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                <button className="btn btn-primary btn-sm" disabled={!draft.trim() || !!busy} onClick={post}><Icon name="send" size={13} />{busy === "post" ? "Posting…" : r.source === "google" ? "Post reply on Google" : "Save reply"}</button>
                <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={writeAI}><Icon name="bolt" size={13} />{busy === "ai" ? "Writing…" : draft ? "Rewrite with AI" : "Write with AI"}</button>
                <span className="faint" style={{ fontSize: 11.5, alignSelf: "center" }}>AI drafts use 1 credit. Nothing posts until you press Post.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {!isPrivate && (
        <div className="rv-foot">
          <button className="rv-link" disabled={!!busy} onClick={() => toggle("featured")}>{r.featured ? "Unfeature" : "Feature on website"}</button>
          <button className="rv-link" disabled={!!busy} onClick={() => toggle("hidden")} title="Hidden reviews still count toward your published average">{r.hidden ? "Show on website" : "Hide from website"}</button>
          {low && !r.reply_text && <button className="rv-link" disabled={!!busy} onClick={resolve}>{r.resolved_at ? "Reopen" : "Mark resolved"}</button>}
          {["manual", "facebook", "other"].includes(r.source) && <button className="rv-link" style={{ color: "var(--red)" }} disabled={!!busy} onClick={remove}>Delete</button>}
          {r.review_url && <a className="rv-link" href={r.review_url} target="_blank" rel="noopener">View original</a>}
        </div>
      )}
    </div>
  );
}

function AddReviewModal({ onClose, onSaved }: any) {
  const DB: any = (window as any).VFP_DB;
  const [f, setF] = React.useState<any>({ source: "facebook", rating: 5, author: "", text: "", date: new Date().toISOString().slice(0, 10), url: "", attest: false });
  const [busy, setBusy] = React.useState<any>(false);
  const set = (p: any) => setF((x: any) => ({ ...x, ...p }));
  const save = async () => {
    setBusy(true);
    try { await DB.reviewAdd(f); notify("Review added."); onSaved(); }
    catch (e: any) { notify(e.message || "Couldn't add it", { kind: "error" }); }
    setBusy(false);
  };
  return ReactDOM.createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(520px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Add a review</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>For reviews customers left somewhere we can't sync — Facebook, Nextdoor, Angi, a card in the mail. Google reviews sync on their own.</p>
        <div style={{ display: "grid", gap: 12 }}>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 160px" }}><label className="field-label">Where it was posted</label>
              <select className="select" value={f.source} onChange={(e: any) => set({ source: e.target.value })}>
                <option value="facebook">Facebook</option><option value="other">Other site (Nextdoor, Angi, Yelp…)</option><option value="manual">In person / letter / email</option>
              </select></div>
            <div style={{ flex: "1 1 140px" }}><label className="field-label">Date</label>
              <input className="input" type="date" value={f.date} onChange={(e: any) => set({ date: e.target.value })} /></div>
          </div>
          <div><label className="field-label">Rating</label>
            <div className="row gap-8">{[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" className={"filter-chip" + (f.rating === n ? " on" : "")} onClick={() => set({ rating: n })}>{n}★</button>
            ))}</div></div>
          <div><label className="field-label">Customer's name</label>
            <input className="input" value={f.author} placeholder="As they signed it" onChange={(e: any) => set({ author: e.target.value })} /></div>
          <div><label className="field-label">What they wrote</label>
            <textarea className="textarea" rows={4} value={f.text} onChange={(e: any) => set({ text: e.target.value })} /></div>
          <div><label className="field-label">Link to the original <span className="faint">(optional)</span></label>
            <input className="input" value={f.url} placeholder="https://…" onChange={(e: any) => set({ url: e.target.value })} /></div>
          <label className="row gap-8" style={{ alignItems: "flex-start", fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={f.attest} onChange={(e: any) => set({ attest: e.target.checked })} style={{ marginTop: 3 }} />
            <span>This is a real review a customer gave us, copied word for word. <span className="faint">Made-up or edited reviews break FTC rules and can cost you your Google listing.</span></span>
          </label>
        </div>
        <div className="row gap-8" style={{ justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !f.author.trim() || !f.attest} onClick={save}>{busy ? "Saving…" : "Add review"}</button>
        </div>
      </div>
    </div>, document.body);
}

/* ---------------- Past-customer campaign ---------------- */

/* Minimal CSV reader: quoted fields, commas and newlines inside quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cur = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cur); rows.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim()));
}
/* Map spreadsheet columns by header name — QuickBooks, Jobber and Excel
   exports all name them a little differently. */
function csvToCustomers(text: string) {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];
  const h = rows[0].map((x) => x.toLowerCase().trim());
  const col = (re: RegExp, not?: RegExp) => h.findIndex((x) => re.test(x) && !(not && not.test(x)));
  const iName = col(/^(customer|client)?\s*(full\s*)?name|^customer$|^client$/);
  const iFirst = col(/first/), iLast = col(/last/, /date|job|service/);
  const iEmail = col(/e-?mail/), iPhone = col(/phone|mobile|cell/);
  const iAddr = col(/address|street/), iDate = col(/date|completed|install|finished/), iJob = col(/job|service|fence|type|description/, /date/);
  return rows.slice(1).map((r) => ({
    name: (iName >= 0 ? r[iName] : [iFirst >= 0 ? r[iFirst] : "", iLast >= 0 ? r[iLast] : ""].join(" ")).trim(),
    email: iEmail >= 0 ? r[iEmail].trim() : "",
    phone: iPhone >= 0 ? r[iPhone].trim() : "",
    address: iAddr >= 0 ? r[iAddr].trim() : "",
    lastJob: iDate >= 0 ? r[iDate].trim() : "",
    job: iJob >= 0 ? r[iJob].trim() : "",
  })).filter((c) => c.name && (c.email || c.phone));
}

function ReviewCampaigns({ live, go, store }: any) {
  const DB: any = (window as any).VFP_DB;
  const settings = store.settings || {};
  const [campaigns, setCampaigns] = React.useState<any>(null);
  const [building, setBuilding] = React.useState<any>(false);
  const load = React.useCallback(async () => {
    if (!live) { setCampaigns([]); return; }
    try { setCampaigns((await DB.reviewCampaigns()).campaigns || []); } catch (e) { setCampaigns([]); }
  }, [live]);
  React.useEffect(() => { load(); }, [load]);
  const stop = async (c: any) => {
    if (!(await confirmDialog({ title: `Stop "${c.name}"?`, body: "Messages that haven't gone out yet are cancelled. Anyone already asked keeps their link.", confirmLabel: "Stop campaign", danger: true }))) return;
    try { await DB.reviewCampaignStop(c.id); notify("Campaign stopped."); load(); } catch (e: any) { notify(e.message, { kind: "error" }); }
  };

  if (building) return <CampaignBuilder live={live} settings={settings} go={go} onDone={() => { setBuilding(false); load(); }} onCancel={() => setBuilding(false)} />;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card pad rv-hero">
        <div style={{ maxWidth: 640 }}>
          <span className="kicker kicker-teal">Reactivation</span>
          <h2 className="display" style={{ fontSize: 22, margin: "6px 0 8px" }}>Your past customers are your fastest path to more reviews.</h2>
          <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            Most happy customers were never asked. One campaign sends each of them a short, personal note with their own feedback link — up to three touches over ten days, a few dozen a day so it feels personal and your phone stays manageable. It stops for anyone who answers.
          </p>
          <div className="rv-rules">
            <span><Icon name="check" size={13} />Every rating gets the same public-review link — no filtering out unhappy customers</span>
            <span><Icon name="check" size={13} />No gift cards or discounts for reviews — Google and the FTC ban it</span>
            <span><Icon name="check" size={13} />Texts only go to customers who opted in; everyone else gets email</span>
          </div>
        </div>
        <div className="rv-hero-cta">
          <button className="btn btn-primary" disabled={!live} onClick={() => setBuilding(true)}><Icon name="send" size={15} />Start a campaign</button>
          {!live && <span className="faint" style={{ fontSize: 12 }}>Connect to the service to launch.</span>}
          {!settings.googleReviewUrl && <span className="faint" style={{ fontSize: 12, color: "var(--amber)" }}>Add your Google review link first, so the public-review option has somewhere to go.</span>}
          {!((store.company && store.company.address) || settings.companyCity) && <span className="faint" style={{ fontSize: 12, color: "var(--amber)" }}>Add your business address in Settings — every marketing email has to show it (CAN-SPAM).</span>}
        </div>
      </div>

      <div className="card pad">
        <div className="kicker" style={{ marginBottom: 10 }}>Campaigns</div>
        {!campaigns ? <p className="muted">Loading…</p> : !campaigns.length ? (
          <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>No campaigns yet. A typical first campaign to a few hundred past customers brings in dozens of new reviews in two weeks.</p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {campaigns.map((c: any) => {
              const pct = c.audience ? Math.round((100 * c.responded) / c.audience) : 0;
              return (
                <div key={c.id} className="rv-camp">
                  <div>
                    <b>{c.name}</b>
                    <div className="faint" style={{ fontSize: 12 }}>{fmtDate(c.launchedAt)} · {c.channel === "both" ? "email + text" : c.channel} · {c.audience} customers · {c.steps} touch{c.steps === 1 ? "" : "es"}</div>
                  </div>
                  <div className="rv-camp-stats">
                    <span><b className="tnum">{c.sent}</b> sent</span>
                    <span><b className="tnum">{c.scheduled}</b> queued</span>
                    <span title="Rated, left a note or reviewed since launch"><b className="tnum" style={{ color: "var(--teal)" }}>{c.responded}</b> responded ({pct}%)</span>
                    {c.failed > 0 && <span style={{ color: "var(--red)" }}><b className="tnum">{c.failed}</b> failed</span>}
                  </div>
                  {c.isOn && c.scheduled > 0 ? <button className="btn btn-ghost btn-sm" onClick={() => stop(c)}>Stop</button> : <span className="chip">{c.isOn ? "Sending done" : "Stopped"}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignBuilder({ live, settings, go, onDone, onCancel }: any) {
  const DB: any = (window as any).VFP_DB;
  const [channel, setChannel] = React.useState<any>("email");
  const [aud, setAud] = React.useState<any>(null);
  const [picked, setPicked] = React.useState<any>({});
  const [steps, setSteps] = React.useState<any>([]);
  const [perDay, setPerDay] = React.useState<any>(40);
  const [name, setName] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>("");
  const fileRef = React.useRef<any>(null);

  const loadAudience = React.useCallback(async (ch: string) => {
    setAud(null);
    try {
      const a = await DB.reviewAudience(ch);
      setAud(a);
      const p: any = {}; (a.eligible || []).forEach((c: any) => { p[c.id] = true; }); setPicked(p);
    } catch (e: any) { notify(e.message, { kind: "error" }); setAud({ eligible: [], excluded: {} }); }
  }, []);
  React.useEffect(() => { loadAudience(channel); DB.reviewCampaignSteps(channel === "both" ? "email" : channel).then((d: any) => setSteps(d.steps || [])).catch(() => {}); }, [channel]);

  const importCsv = async (file: any) => {
    if (!file) return;
    const rows = csvToCustomers(await file.text());
    if (!rows.length) { notify("Couldn't find names with an email or phone in that file. It needs a header row (Name, Email, Phone…).", { kind: "error" }); return; }
    setBusy("import");
    try {
      const r = await DB.reviewImportPast(rows);
      notify(`Imported ${r.added} new and matched ${r.matched} existing customers${r.skipped ? ` (${r.skipped} skipped)` : ""}.`);
      await loadAudience(channel);
    } catch (e: any) { notify(e.message, { kind: "error" }); }
    setBusy("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const days = Math.max(1, Math.ceil(ids.length / (perDay || 40)));
  const setStep = (i: number, p: any) => setSteps((s: any[]) => s.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const launch = async () => {
    const ok = await confirmDialog({
      title: `Ask ${ids.length} past customer${ids.length === 1 ? "" : "s"} for a review?`,
      body: `${perDay} a day over about ${days} day${days === 1 ? "" : "s"}, up to ${steps.length} touches each. It stops for anyone who rates, reviews or replies.`,
      confirmLabel: "Launch campaign",
    });
    if (!ok) return;
    setBusy("launch");
    try {
      const r = await DB.reviewCampaignLaunch({ name, channel, customerIds: ids, steps, perDay });
      notify(`Campaign launched — ${r.campaign.queued} customers queued${r.campaign.skipped ? `, ${r.campaign.skipped} skipped` : ""}.`);
      onDone();
    } catch (e: any) { notify(e.message, { kind: "error" }); }
    setBusy("");
  };

  const ex = (aud && aud.excluded) || {};
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <button className="btn btn-ghost btn-sm" style={{ justifySelf: "start" }} onClick={onCancel}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />Campaigns</button>

      <div className="card pad">
        <div className="kicker" style={{ marginBottom: 10 }}>1 · How to reach them</div>
        <div className="cat-tabs" style={{ margin: 0 }}>
          {[["email", "Email (recommended)"], ["sms", "Text — opted-in only"], ["both", "Email + text"]].map(([id, lb]) => (
            <button key={id} className={"filter-chip" + (channel === id ? " on" : "")} onClick={() => setChannel(id)}>{lb}</button>
          ))}
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "10px 0 0" }}>Past customers rarely opted in to marketing texts, so email reaches the most people. Anyone who texted STOP or unsubscribed is skipped automatically.</p>
      </div>

      <div className="card pad">
        <div className="row between" style={{ gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <div className="kicker">2 · Who to ask</div>
          <div className="row gap-8">
            <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(e: any) => importCsv(e.target.files && e.target.files[0])} />
            <button className="btn btn-ghost btn-sm" disabled={busy === "import"} onClick={() => fileRef.current && fileRef.current.click()}><Icon name="download" size={13} style={{ transform: "rotate(180deg)" }} />{busy === "import" ? "Importing…" : "Import past customers (CSV)"}</button>
          </div>
        </div>
        <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>Everyone with a finished job, plus anyone you import from your old system (QuickBooks, Jobber, a spreadsheet — Name, Email, Phone, Address, Job date).</p>
        {!aud ? <p className="muted">Finding past customers…</p> : (
          <>
            <div className="rv-excl">
              <span><b>{aud.eligible.length}</b> can be asked</span>
              {ex.reviewed > 0 && <span><b>{ex.reviewed}</b> already reviewed</span>}
              {ex.askedRecently > 0 && <span><b>{ex.askedRecently}</b> asked in the last 60 days</span>}
              {ex.tooRecent > 0 && <span><b>{ex.tooRecent}</b> finished this week (the automatic ask covers them)</span>}
              {ex.noContact > 0 && <span><b>{ex.noContact}</b> no {channel === "sms" ? "phone" : "email"}</span>}
            </div>
            {!aud.eligible.length ? (
              <EmptyState icon="user" title="No past customers to ask yet" sub="Import a list from your old system, or finish a few jobs in Fence Works." />
            ) : (
              <div className="rv-aud">
                <label className="rv-aud-row rv-aud-head">
                  <input type="checkbox" checked={ids.length === aud.eligible.length} onChange={(e: any) => { const p: any = {}; aud.eligible.forEach((c: any) => { p[c.id] = e.target.checked; }); setPicked(p); }} />
                  <span>{ids.length} of {aud.eligible.length} selected</span><span>Last job</span><span>Reach</span>
                </label>
                {aud.eligible.slice(0, 400).map((c: any) => (
                  <label key={c.id} className="rv-aud-row">
                    <input type="checkbox" checked={!!picked[c.id]} onChange={(e: any) => setPicked((p: any) => ({ ...p, [c.id]: e.target.checked }))} />
                    <span><b>{c.name}</b>{c.job ? <span className="faint"> · {c.job}</span> : null}{c.imported ? <span className="chip" style={{ marginLeft: 6, fontSize: 10 }}>imported</span> : null}</span>
                    <span className="faint">{c.lastJobAt ? fmtDate(c.lastJobAt) : "—"}</span>
                    <span className="faint">{[c.email && "email", c.phone && "phone"].filter(Boolean).join(" + ")}</span>
                  </label>
                ))}
                {aud.eligible.length > 400 && <p className="faint" style={{ fontSize: 12, padding: "8px 10px" }}>+{aud.eligible.length - 400} more (included when selected).</p>}
              </div>
            )}
          </>
        )}
      </div>

      <div className="card pad">
        <div className="kicker" style={{ marginBottom: 6 }}>3 · What they get</div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>
          <code>{"{{rate_link}}"}</code> is each customer's own feedback page: they tap a star, then choose to post a public review or send you a private note — the same choice for every rating. Also available: <code>{"{{customer}}"}</code> (first name), <code>{"{{company}}"}</code>, <code>{"{{address}}"}</code>.
        </p>
        <div style={{ display: "grid", gap: 12 }}>
          {steps.map((st: any, i: number) => (
            <div key={i} className="rv-step">
              <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
                <b style={{ fontSize: 13 }}>Touch {i + 1}{i === 0 ? " · right away" : ""}</b>
                {i > 0 && <label className="row gap-8" style={{ fontSize: 12.5, alignItems: "center" }}>Day <input className="input" type="number" min={1} max={60} value={st.delayDays} style={{ width: 70 }} onChange={(e: any) => setStep(i, { delayDays: Number(e.target.value) })} /></label>}
                {steps.length > 1 && i > 0 && <button className="rv-link" onClick={() => setSteps((s: any[]) => s.filter((_, j) => j !== i))}>Remove</button>}
              </div>
              {channel !== "sms" && <input className="input" style={{ marginTop: 8 }} value={st.subject} placeholder="Subject" onChange={(e: any) => setStep(i, { subject: e.target.value })} />}
              <textarea className="textarea" style={{ marginTop: 8 }} rows={channel === "sms" ? 3 : 7} value={st.body} onChange={(e: any) => setStep(i, { body: e.target.value })} />
              {channel === "sms" && <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{st.body.length} characters{st.body.length > 160 ? " · sends as more than one text" : ""}</div>}
              {i === 0 && !/\{\{(rate|review)_link\}\}/.test(st.body) && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 4 }}>The first message needs {"{{rate_link}}"}.</div>}
              {/gift card|\$\d+ (off|credit)|discount|% off/i.test(st.body) && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 4 }}>Offering something in exchange for a review breaks Google's rules and the FTC's. Take it out.</div>}
            </div>
          ))}
          {steps.length < 4 && <button className="btn btn-ghost btn-sm" style={{ justifySelf: "start" }} onClick={() => setSteps((s: any[]) => [...s, { delayDays: (s[s.length - 1]?.delayDays || 0) + 5, subject: "", body: "" }])}><Icon name="plus" size={13} />Add a touch</button>}
        </div>
      </div>

      <div className="card pad">
        <div className="kicker" style={{ marginBottom: 10 }}>4 · Pace and launch</div>
        <div className="row gap-12" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label className="field-label">Campaign name</label>
            <input className="input" value={name} placeholder="Fall 2026 past customers" onChange={(e: any) => setName(e.target.value)} /></div>
          <div><label className="field-label">Customers a day</label>
            <div className="row gap-8">{[20, 40, 80].map((n) => <button key={n} className={"filter-chip" + (perDay === n ? " on" : "")} onClick={() => setPerDay(n)}>{n}</button>)}</div></div>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={!live || !ids.length || !steps.length || busy === "launch" || !/\{\{(rate|review)_link\}\}/.test((steps[0] && steps[0].body) || "")} onClick={launch}>
            <Icon name="send" size={15} />{busy === "launch" ? "Launching…" : `Launch to ${ids.length}`}
          </button>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "10px 0 0" }}>A steady {perDay}/day looks natural to Google (a sudden burst of reviews can get filtered) and keeps callbacks manageable. About {days} day{days === 1 ? "" : "s"} to reach everyone.</p>
      </div>
    </div>
  );
}

/* ---------------- Share & website ---------------- */
function ReviewsShare({ store, live, s, go }: any) {
  const DB: any = (window as any).VFP_DB;
  const settings = store.settings || {};
  const w = settings.widget || {};
  const token = w.token || "";
  const api = (DB && DB.apiBase && DB.apiBase()) || "https://api.fenceworks.ai";
  const base = api.replace(/\/$/, "");
  const pageUrl = token ? `${base}/public/reviews/${token}/page` : "";
  const feedUrl = token ? `${base}/public/reviews/${token}` : "";
  const embed = pageUrl ? `<iframe src="${pageUrl}" title="Customer reviews" style="width:100%;height:900px;border:0" loading="lazy"></iframe>` : "";
  const copy = (t: string, what: string) => { try { navigator.clipboard.writeText(t); notify(what + " copied."); } catch (e) { notify("Copy failed — select and copy it by hand.", { kind: "error" }); } };
  const mint = () => store.setSettings({ widget: { ...w, token: genToken().slice(0, 12) } });
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card pad">
        <span className="kicker kicker-teal">Your reviews page</span>
        <h2 className="display" style={{ fontSize: 20, margin: "6px 0 6px" }}>A page with every public review, always up to date.</h2>
        <p className="muted" style={{ fontSize: 13.5, margin: "0 0 14px", maxWidth: 680 }}>Link it from texts, emails, your truck wrap QR code and your social bios. It shows your real average (every public review counts, even ones you hide from the list), owner replies, and buttons to leave a review or get a quote. Private feedback never appears.</p>
        {!token ? (
          <button className="btn btn-primary btn-sm" onClick={mint}><Icon name="plus" size={14} />Create my reviews page</button>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="rv-url"><code>{pageUrl}</code><button className="btn btn-ghost btn-sm" onClick={() => copy(pageUrl, "Link")}>Copy</button><a className="btn btn-outline btn-sm" href={pageUrl} target="_blank" rel="noopener">Open</a></div>
            {!live && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Live once you're connected to the service.</p>}
          </div>
        )}
      </div>

      {token && (
        <div className="card pad">
          <span className="kicker">On your website</span>
          <h3 className="display" style={{ fontSize: 17, margin: "6px 0 6px" }}>Put your live reviews on your own site</h3>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 12px" }}>Paste this where you want the reviews to appear (a "Reviews" page works best). It updates by itself as new reviews come in.</p>
          <div className="rv-url"><code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{embed}</code><button className="btn btn-ghost btn-sm" onClick={() => copy(embed, "Embed code")}>Copy</button></div>
          <details style={{ marginTop: 12 }}>
            <summary className="faint" style={{ fontSize: 12.5, cursor: "pointer" }}>For your web developer: the JSON feed</summary>
            <p className="faint" style={{ fontSize: 12, margin: "8px 0" }}>Public reviews + the real average, CORS-open, cached 5 minutes. Render it server-side for SEO. Don't mark Google reviews up as <code>Review</code>/<code>AggregateRating</code> structured data on your own site — Google treats reviews aggregated from other sites as spammy markup.</p>
            <div className="rv-url"><code>{feedUrl}</code><button className="btn btn-ghost btn-sm" onClick={() => copy(feedUrl, "Feed URL")}>Copy</button></div>
          </details>
        </div>
      )}

      <div className="card pad rv-hero">
        <div style={{ maxWidth: 640 }}>
          <span className="kicker kicker-teal">Coming next</span>
          <h3 className="display" style={{ fontSize: 17, margin: "6px 0 6px" }}>A complete website, built and hosted for you</h3>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>Service pages, town pages, your photos, this live reviews wall, and the instant-quote widget — generated from your Fence Works account and hosted on your own domain.</p>
        </div>
        <div className="rv-hero-cta"><button className="btn btn-ghost btn-sm" onClick={() => go("gbp")}><Icon name="target" size={14} />Check your Google Profile score</button></div>
      </div>
    </div>
  );
}

Object.assign(window, { ReviewsPage });
export { ReviewsPage, csvToCustomers, parseCsv };
